import { Component, OnDestroy, OnInit, ViewChild } from "@angular/core";
import { UntypedFormGroup } from "@angular/forms";
import { MatTabChangeEvent, MatTabGroup } from "@angular/material/tabs";
import { ActivatedRoute, Router } from "@angular/router";
import { Store } from "@ngxs/store";
import { DialogService } from "odq-toolbox";
import { Subject } from "rxjs";
import { takeUntil } from "rxjs/operators";
import { AuthUser } from "~app/core/auth/core/auth-user.model";
import { AuthState } from "~app/core/auth/core/auth.state";
import { PaymentReceipt } from "~app/inscription/store/cotisation-form/inscription.model";
import { SubscriptionState } from "../store/subscription";
import {
  LoadMemberId,
  LoadSubscriptionForm,
  LoadSubscriptionPeriods,
  LoadSubscriptionStatus,
  ReportReclamationValidation,
} from "../store/subscription/subscription.actions";
import {
  PaymentErrorModel,
  SelectedPremiumInfo,
  SubscriptionFormViewModel,
  SubscriptionNoticePreselection,
  SubscriptionPeriod,
} from "../store/subscription/subscription.model";
import { ReclamationValidationDialogComponent } from "./reclamation-validation/reclamation-validation-dialog.component";
import { RestartFormDialogComponent } from "./restart-form-dialog/restart-form-dialog.component";

@Component({
    selector: "odq-subscription-form-farp",
    templateUrl: "./subscription-form.component.html",
    styleUrls: ["./subscription-form.component.scss"],
    standalone: false
})
export class SubscriptionFormComponent implements OnInit, OnDestroy {
  selectedIndex = 0;
  previousIndex = 0;
  formGroup: UntypedFormGroup;
  ready = true;

  user: AuthUser;
  private destroy$ = new Subject<void>();
  user$ = this.store.select<AuthUser>(AuthState.user);
  model$ = this.store.select<SubscriptionFormViewModel>(
    SubscriptionState.formModel
  );
  periods$ = this.store.select<SubscriptionPeriod[]>(SubscriptionState.periods);
  loadingMemberId$ = this.store.select<boolean>(
    SubscriptionState.loadingMemberId
  );
  loadingPeriods$ = this.store.select<boolean>(
    SubscriptionState.loadingPeriods
  );
  loadingPremiumData$ = this.store.select<boolean>(
    SubscriptionState.loadingPremiumData
  );
  paymentErrorInfo$ = this.store.select<PaymentErrorModel>(
    SubscriptionState.paymentErrorInfo
  );
  isSubscriptionBlocked$ = this.store.select<boolean>(
    SubscriptionState.isSubscriptionBlocked
  );

  selectedPeriod: SubscriptionPeriod;
  selectedPremium: SelectedPremiumInfo;
  model: SubscriptionFormViewModel;
  paymentReceipt$ = this.store.select<PaymentReceipt>(
    SubscriptionState.paymentReceipt
  );
  memberId$ = this.store.select<string>(SubscriptionState.memberId);
  memberId: string;
  userCanAddSurplus = false;
  userHasDecidedSurplus = false;

  subscriptionNoticeId: string;
  year: string;

  userCanEditForm$ = this.store.select<boolean>(
    SubscriptionState.userCanEditForm
  );
  userCanEditForm: boolean = null;

  subscriptionIsBlocked$ = this.store.select<boolean>(
    SubscriptionState.isSubscriptionBlocked
  );
  subscriptionIsBlocked: boolean = null;

  subscriptionNoticePreselection$ =
    this.store.select<SubscriptionNoticePreselection>(
      SubscriptionState.subscriptionNoticePreselection
    );
  isReclamationValidated = false;

  savingReportReclamationValidation$ = this.store.select<boolean>(
    SubscriptionState.savingReportReclamationValidation
  );
  inactiveMemberForSubscription$ = this.store.select<boolean>(
    AuthState.inactiveMemberForSubscription
  );

  paymentReceipt: PaymentReceipt;
  @ViewChild("tabs") tabGroup: MatTabGroup;
  confirmationPaymentTab = 2;

  constructor(
    private route: ActivatedRoute,
    private store: Store,
    private router: Router,
    private dialog: DialogService
  ) {}

  ngOnInit(): void {
    this.year = this.route.snapshot.paramMap.get("year");
    this.subscriptionNoticeId = this.route.snapshot.paramMap.get("noticeId");

    this.selectedPremium = new SelectedPremiumInfo();
    this.selectedPeriod = null;
    this.user$.pipe(takeUntil(this.destroy$)).subscribe((u) => {
      if (u) {
        this.user = u;
        this.store.dispatch(new LoadMemberId(u.permitNumber));
      }
    });
    this.memberId$.pipe(takeUntil(this.destroy$)).subscribe((m) => {
      if (m) {
        this.memberId = m;
        this.store.dispatch(new LoadSubscriptionPeriods(m));
        this.store.dispatch(new LoadSubscriptionStatus(m));
      }
    });
    this.periods$.pipe(takeUntil(this.destroy$)).subscribe((p) => {
      if (p && p.length > 0) {
        //select period according
        if (this.subscriptionNoticeId) {
          this.selectedPeriod = p.find(
            (i) =>
              i.editable && i.subscriptionNoticeId == this.subscriptionNoticeId
          );
        } else {
          this.selectedPeriod = p.find(
            (i) => i.editable && i.year == parseInt(this.year)
          );
        }
        this.loadFormData();
      }
    });
    this.model$.pipe(takeUntil(this.destroy$)).subscribe((m) => {
      if (m) {
        this.model = m;
        this.initSelectedPremium(m);
      }
    });
    this.paymentReceipt$.pipe(takeUntil(this.destroy$)).subscribe((p) => {
      if (p) {
        this.paymentReceipt = p;
        this.goToConfirmationPayment();
      }
    });
    this.userCanEditForm$.pipe(takeUntil(this.destroy$)).subscribe((p) => {
      this.userCanEditForm = p;
    });
    this.subscriptionIsBlocked$
      .pipe(takeUntil(this.destroy$))
      .subscribe((p) => {
        this.subscriptionIsBlocked = p;
        if (p) {
          this.redirectToSubscriptionIndex();
        }
      });
    this.paymentErrorInfo$.pipe(takeUntil(this.destroy$)).subscribe((p) => {
      if (p && p.requiresRestartForm) {
        this.dialog
          .open(RestartFormDialogComponent, { errorMessage: p.message })
          .pipe(takeUntil(this.destroy$))
          .subscribe(() => {
            this.redirectToSubscriptionIndex();
          });
      }
    });
    this.subscriptionNoticePreselection$
      .pipe(takeUntil(this.destroy$))
      .subscribe((p) => {
        if (p == null || !this.isReclamationValidated) {
          return;
        }
        this.isReclamationValidated = true;
        if (p.isReclamationReported === true) {
          this.redirectToSubscriptionIndex();
        }
        if (p.isReclamationReported === false) {
          this.selectedPremium.subscriptionNoticeId = p.subscriptionNoticeId;
          this.nextStep();
        }
      });
  }
  redirectToSubscriptionIndex() {
    this.router.navigateByUrl("/farp-subscription/index");
  }

  initSelectedPremium(model: SubscriptionFormViewModel) {
    this.selectedPremium = new SelectedPremiumInfo();
    this.selectedPremium.permitNumber = this.user.permitNumber;
    this.selectedPremium.surplusId = null;
    this.selectedPremium.userRefuseSurplus = null; //default (so screen does not show yet any selected)
    this.userHasDecidedSurplus = false;

    if (model && this.selectedPeriod) {
      this.selectedPremium.memberId = this.model.memberId;
      this.selectedPremium.subscriptionPeriodId = this.selectedPeriod.id;
      this.selectedPremium.year = this.selectedPeriod.year;
      this.selectedPremium.subscriptionNoticeId =
        this.selectedPeriod.subscriptionNoticeId;
      this.selectDefaultSurplus();
      this.selectedPremium.calculateTotals(this.model);
      this.selectedPremium.resetPaymentInfo();
      this.userCanAddSurplus = model.surplus.length > 0;
    }
  }

  selectDefaultSurplus() {
    if (this.model.defaultSurplusToSelect?.premiumId) {
      this.selectedPremium.surplusId =
        this.model.defaultSurplusToSelect?.premiumId;
      this.selectedPremium.userRefuseSurplus = false;
      this.userHasDecidedSurplus = true;
    }
  }

  loadFormData() {
    if (this.selectedPeriod && this.user) {
      this.store.dispatch(
        new LoadSubscriptionForm(
          this.selectedPeriod.id,
          this.selectedPeriod.subscriptionNoticeId,
          this.memberId
        )
      );
    }
  }

  ngOnDestroy() {
    this.destroy$.next();
    this.destroy$.complete();
  }

  tabChanged(tabChangeEvent: MatTabChangeEvent) {
    this.selectedIndex = tabChangeEvent.index;
    this.previousIndex = Number(this.selectedIndex);
  }

  previousStep() {
    this.previousIndex = Number(this.selectedIndex);
    this.selectedIndex -= 1;
  }

  validateSurplus() {
    const isSurplusSelected = !this.selectedPremium.userRefuseSurplus;
    const noDefaultSurplus = this.model.defaultSurplusToSelect == null;
    const defaultSurplusAmount =
      this.model.defaultSurplusToSelect?.premiumAmount;
    const selectedSurplusAmount = this.selectedPremium.premiumAmount;
    const askForReclamationValidation =
      isSurplusSelected &&
      (noDefaultSurplus || selectedSurplusAmount > defaultSurplusAmount);
    const memberAlreadyAnsweredReclamationValidation =
      this.selectedPeriod?.isWithNonReportedReclamation != null;
    var isExtension = this.model.isExtension;
    this.isReclamationValidated = false;

    if (
      askForReclamationValidation &&
      !memberAlreadyAnsweredReclamationValidation &&
      !isExtension
    ) {
      this.showAskForReclamationValidationDialog();
    } else {
      this.nextStep();
    }
  }

  showAskForReclamationValidationDialog() {
    this.dialog
      .open(ReclamationValidationDialogComponent, {}, { width: "600px" })
      .pipe(takeUntil(this.destroy$))
      .subscribe((result) => {
        this.isReclamationValidated = true;
        this.store.dispatch(
          new ReportReclamationValidation(
            this.memberId,
            result.isReclamationReported,
            this.selectedPremium.surplusId,
            this.selectedPremium.year,
            this.subscriptionNoticeId
          )
        );
      });
  }
  nextStep() {
    if (this.checkValidity(this.selectedIndex)) {
      this.previousIndex = Number(this.selectedIndex);
      this.selectedIndex += 1;
    }
  }

  userDecidedSurplus(event) {
    this.userHasDecidedSurplus = true;
  }

  checkValidity(tabIndex: number) {
    return true;
  }

  goToConfirmationPayment() {
    if (this.tabGroup) {
      this.selectedIndex = this.confirmationPaymentTab;
      this.tabGroup.selectedIndex = this.confirmationPaymentTab;
    }
  }
}
